import {
  ChangeEvent,
  FC,
  useCallback,
  useMemo,
  useState,
  WheelEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { FieldArray, useFormikContext } from "formik";
import styled from "styled-components";

import { Button, ButtonTheme } from "@components/Button";
import { ErrorRenderer, FormError } from "@components/FormError";
import { Icon } from "@components/Icon";
import { Input } from "@components/Input";
import { Label } from "@components/Label";
import { MultiSelect } from "@components/MultiSelect";
import { device } from "@styles/breakpoints";
import { AssetFamily, ASSETS_FAMILY, Nullable, OptionalNotNull } from "@utils";
import { MAX_FEE_VALUE, MIN_FEE_VALUE } from "./consts";
import { TCreateAuctionFormValues } from "./CreateAuctionForm";

const Container = styled.div`
  width: 100%;
`;

const ErrorWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: ${({ theme }) => theme.tripleMargin};
`;

const InlineError = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: -2px;
`;

const MultiSelectContainer = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
`;

const Title = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 12rem 12rem;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.tripleMargin};

  @media ${device.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const TitleButtonContainer = styled.div`
  @media ${device.tablet} {
    width: 100%;

    & button {
      width: 100%;
      height: 40px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(10rem, 1fr) 1fr 1fr 1fr;
  gap: 20px;
  align-items: start;
  margin-bottom: ${({ theme }) => theme.tripleMargin};
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media (max-width: 1060px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media ${device.tablet} {
    grid-template-columns: 1fr; /* stack vertically on small screens */
    gap: 12px; /* a bit tighter spacing */
    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media ${device.mobileL} {
    grid-template-columns: 1fr; /* stack vertically on mobile */
    gap: 8px;
    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }
`;

const ButtonRow = styled.div`
  display: none;

  @media (max-width: 1200px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media (max-width: 1060px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media ${device.tablet} {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  box-sizing: border-box;
  height: 46px;
`;

const LabelBox = styled.div`
  display: flex;
  align-items: center;
  padding-right: 15px;

  & ${Label} {
    margin-right: auto;
  }
`;

const TooltipContent = styled.div`
  max-width: 20rem;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;

  & button {
    width: 100%;
    height: 40px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const EMPTY_STATE = {
  assets: [] as AssetFamily[],
  fee: undefined as OptionalNotNull<number>,
};

interface Props {
  disabled?: boolean;
}

export const AuctioneerFeeByAssetField: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const {
    values: { auctioneerFees },
    setFieldValue,
  } = useFormikContext<TCreateAuctionFormValues>();

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<Nullable<typeof EMPTY_STATE>>(null);

  // Error validation logic
  const hasMaxFeeValue: boolean = auctioneerFees.some(
    ({ fee }) => fee && fee > MAX_FEE_VALUE
  );
  const hasMinFeeValue: boolean = auctioneerFees.some(
    ({ fee }) => fee && fee < MIN_FEE_VALUE
  );

  const assetsFamilyListAtIndex = useCallback(
    (index: number) =>
      ASSETS_FAMILY.filter(
        (asset: AssetFamily) =>
          !auctioneerFees.find(
            (fee, idx) => fee.assets.includes(asset) && index !== idx
          )
      ),
    [auctioneerFees]
  );

  const canAddFee = useMemo(
    () =>
      ASSETS_FAMILY.filter(
        (asset: AssetFamily) =>
          !disabled &&
          !value &&
          !auctioneerFees.find((fee) => fee.assets.includes(asset))
      ).length > 0,
    [auctioneerFees, value, disabled]
  );

  const canSaveFee = useMemo(
    () =>
      value?.assets.length &&
      typeof value?.fee === "number" &&
      value.fee >= MIN_FEE_VALUE &&
      value.fee <= MAX_FEE_VALUE,
    [value]
  );

  const handleAssetsChange = useCallback((id: string) => {
    const asset = id as AssetFamily;

    setValue((currentValue) => {
      if (!currentValue) return { assets: [asset], fee: undefined };

      let updatedAssets = [...currentValue.assets];

      if (updatedAssets.includes(asset)) {
        updatedAssets = updatedAssets.filter((a) => a !== asset);
      } else {
        updatedAssets.push(asset);
      }

      return { ...currentValue, assets: updatedAssets };
    });
  }, []);

  const handleFeeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let inputValue = event.currentTarget.value;

      // Remove leading zeros but preserve decimal numbers like 0.321
      if (
        inputValue.length > 1 &&
        inputValue.startsWith("0") &&
        !inputValue.includes(".")
      ) {
        inputValue = inputValue.replace(/^0+/, "") || "0";
        event.currentTarget.value = inputValue;
      }

      const fee = inputValue === "" ? undefined : Number(inputValue);

      setValue((currentValue) => {
        if (!currentValue) return { fee, assets: [] };
        return { ...currentValue, fee };
      });
    },
    []
  );

  const handleSaveFee = useCallback(
    (index: number) => {
      const currentFees = auctioneerFees;
      currentFees[index] = value!;

      setFieldValue("auctioneerFees", currentFees);
      setIsEditing(false);
      setValue(null);
    },
    [value, auctioneerFees, setFieldValue]
  );

  const shouldShowSaveButton = useCallback(
    (index: number) => index === auctioneerFees.length - 1 && isEditing,
    [isEditing, auctioneerFees]
  );

  const canRemoveFee = useCallback(
    (index: number) => !isEditing || shouldShowSaveButton(index),
    [isEditing, shouldShowSaveButton]
  );

  return (
    <>
      <FieldArray
        name="auctioneerFees"
        render={(helpers) => (
          <>
            <Title>
              <LabelBox>
                <Label className="mr-6">
                  {t("auctionForm.auctioneerFee")} &#42;
                </Label>
                <div id="auctioneer-fee-tooltip">
                  <Icon.InfoCircle />
                </div>
              </LabelBox>
              <TitleButtonContainer>
                <Button
                  type="button"
                  theme={ButtonTheme.Blue}
                  disabled={!canAddFee || isEditing}
                  onClick={() => {
                    setIsEditing(true);
                    helpers.push(EMPTY_STATE);
                  }}
                >
                  {t<string>("addFee")}
                </Button>
              </TitleButtonContainer>
            </Title>
            {auctioneerFees && (
              <>
                {auctioneerFees.length > 0 && (
                  <Container>
                    {auctioneerFees.map((_, index) => {
                      const removeButton = (
                        <ButtonContainer>
                          <Button
                            type="button"
                            theme={ButtonTheme.Quaternary}
                            disabled={!canRemoveFee(index)}
                            onClick={() => {
                              setIsEditing(false);
                              setValue(null);
                              helpers.remove(index);
                            }}
                          >
                            {t<string>("removeFee")}
                          </Button>
                        </ButtonContainer>
                      );

                      const saveButton = shouldShowSaveButton(index) && (
                        <ButtonContainer>
                          <Button
                            type="button"
                            theme={ButtonTheme.Primary}
                            disabled={!canSaveFee}
                            onClick={() => handleSaveFee(index)}
                          >
                            {t<string>("saveFee")}
                          </Button>
                        </ButtonContainer>
                      );

                      const currentFee = shouldShowSaveButton(index)
                        ? value?.fee
                        : auctioneerFees[index].fee;
                      const hasInvalidFee =
                        currentFee !== undefined &&
                        (currentFee < MIN_FEE_VALUE ||
                          currentFee > MAX_FEE_VALUE);

                      return (
                        <div key={index}>
                          <Row>
                            <MultiSelectContainer>
                              <MultiSelect
                                selectAll
                                testId={`SelectAuctioneerFeeAssetFamily.${index}`}
                                disabled={!shouldShowSaveButton(index)}
                                noSelectedTitle={t("selectAssetFamily")}
                                options={assetsFamilyListAtIndex(index).map(
                                  (a) => ({
                                    id: a,
                                    title: a,
                                  })
                                )}
                                selectedIds={
                                  shouldShowSaveButton(index)
                                    ? value?.assets || []
                                    : auctioneerFees[index].assets
                                }
                                toggleOption={handleAssetsChange}
                              />
                            </MultiSelectContainer>
                            <InputContainer>
                              <StyledInput
                                data-testid={`AuctioneerFeeValue.${index}`}
                                type="number"
                                value={
                                  shouldShowSaveButton(index)
                                    ? value?.fee ?? ""
                                    : auctioneerFees[index].fee
                                }
                                placeholder={`${t(
                                  "auctionForm.fee"
                                )} (${MIN_FEE_VALUE}-${MAX_FEE_VALUE}%)`}
                                disabled={!shouldShowSaveButton(index)}
                                onChange={handleFeeChange}
                                onWheel={(e: WheelEvent<HTMLInputElement>) =>
                                  e.currentTarget.blur()
                                }
                                min={MIN_FEE_VALUE}
                                max={MAX_FEE_VALUE}
                                step="0.01"
                                maxLength={6}
                              />
                              {hasInvalidFee && (
                                <InlineError>
                                  {currentFee < MIN_FEE_VALUE
                                    ? `${t(
                                        "auctionForm.errors.fee.min"
                                      )} (Min: ${MIN_FEE_VALUE}%)`
                                    : `${t(
                                        "auctionForm.errors.fee.max"
                                      )} (Max: ${MAX_FEE_VALUE}%)`}
                                </InlineError>
                              )}
                            </InputContainer>
                            {removeButton}
                            {saveButton}
                          </Row>
                          <ButtonRow>
                            {removeButton}
                            {saveButton}
                          </ButtonRow>
                        </div>
                      );
                    })}
                  </Container>
                )}
              </>
            )}
          </>
        )}
      />
      <FormError name="auctioneerFees" />
      {hasMinFeeValue && (
        <ErrorWrapper>
          <ErrorRenderer children="auctionForm.errors.fee.min" />
        </ErrorWrapper>
      )}
      {hasMaxFeeValue && (
        <ErrorWrapper>
          <ErrorRenderer children="auctionForm.errors.fee.max" />
        </ErrorWrapper>
      )}
      <Tooltip anchorId="auctioneer-fee-tooltip">
        <TooltipContent>{t("auctionForm.auctioneerFeeTooltip")}</TooltipContent>
      </Tooltip>
    </>
  );
};
